import React, { useEffect } from 'react';

const Modal = ({ show, onClose, children, title, size = 'md', footer, className = '' }) => {
  // Prevent scrolling of the background when modal is open
  // and track open modal count to handle multiple modals properly
  useEffect(() => {
    // Use a global counter to track open modals
    window.openModalCount = window.openModalCount || 0;
    // Use a global zIndex tracker to ensure proper stacking
    window.modalZIndexBase = window.modalZIndexBase || 1000;
    
    if (show) {
      // Increment counter when a modal opens
      window.openModalCount++;
      // Increment z-index for each new modal
      window.modalZIndexBase += 10;
      document.body.style.overflow = 'hidden';
    } else if (window.openModalCount > 0) {
      // Decrement counter when a modal closes
      window.openModalCount--;
      // Decrease z-index when a modal closes
      window.modalZIndexBase = Math.max(1000, window.modalZIndexBase - 10);
      // Only restore scrolling when all modals are closed
      if (window.openModalCount === 0) {
        document.body.style.overflow = 'auto';
      }
    }
    
    return () => {
      // Clean up when component unmounts
      if (show && window.openModalCount > 0) {
        window.openModalCount--;
        window.modalZIndexBase = Math.max(1000, window.modalZIndexBase - 10);
        if (window.openModalCount === 0) {
          document.body.style.overflow = 'auto';
        }
      }
    };
  }, [show]);
  
  // Calculate size class based on prop
  const sizeClass = 
    size === 'sm' ? 'max-w-md' : 
    size === 'lg' ? 'max-w-4xl' : 
    size === 'xl' ? 'max-w-6xl' : 
    size === '2xl' ? 'max-w-7xl' :
    size === 'full' ? 'max-w-full w-full h-full' : 
    'max-w-2xl';

  if (!show) return null;

  // Get current z-index for this modal instance
  const modalZIndex = window.modalZIndexBase || 1000;
  
  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: modalZIndex }}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity" 
          onClick={onClose}
          aria-hidden="true"
          style={{ zIndex: 1 }}
        >
          <div className="absolute inset-0 bg-gray-900 opacity-50"></div>
        </div>

        {/* Modal Dialog */}
        <div 
          className={`inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle w-full ${sizeClass} ${className} max-h-[90vh] overflow-y-auto border border-gray-100`}
          style={{ zIndex: 10 }}
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="modal-headline"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50 sticky top-0 z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-150 focus:outline-none"
              aria-label="Close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Body */}
          <div className="p-5 overflow-y-auto ${footer ? 'max-h-[calc(80vh-130px)]' : 'max-h-[calc(80vh-60px)]'}">
            {children}
          </div>
          
          {/* Footer */}
          {footer && (
            <div className="px-6 py-4 mt-2 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              {footer}
            </div>
          )}
        </div>
      </div>
      
      {/* Add custom style to improve modal appearance with z-index handling */}
      <style jsx>{`
        :global(.modal-backdrop) {
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
        :global(.modal-content) {
          border-radius: 12px !important;
        }
        :global(.points-structure-modal) {
          margin-top: 15px;
        }
        :global(.dealer-modal) {
          margin-bottom: 15px;
        }
        :global(.modal-high-level), :global(.modal-highest-level) {
          margin: 20px 0;
        }
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15) !important;
        }
        :global(.voucher-modal) {
          max-width: 90vw !important;
          margin: 0.5rem auto !important;
        }
        :global(.modal-highest-level) {
          z-index: 70 !important;
        }
        :global(.modal-high-level) {
          z-index: 65 !important;
        }
        :global(.modal-medium-level) {
          z-index: 60 !important;
        }
      `}</style>
    </div>
  );
};

export default Modal;